import React, { useMemo, useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toOfficialCategoryName } from '@/utils/edtechCategoryNames';
import { getDealCategories } from '@/utils/procurementCategoryUtils';
import { getDealValueUSDForTotals } from '@/utils/procurementValueUsd';
import { getCentroid } from '@/utils/countryCentroids';
import { useTranslation } from '@/components/utils/translations';

const GEO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

/** Normalize country names for matching (deal country -> map geography) */
const COUNTRY_ALIASES = {
  'United Kingdom': ['united kingdom', 'uk', 'great britain', 'england'],
  'USA': ['united states', 'usa', 'us', 'united states of america'],
  'United States': ['united states', 'usa', 'us', 'united states of america'],
  'Australia': ['australia'],
  'New Zealand': ['new zealand'],
  'Canada': ['canada'],
  'France': ['france'],
  'Germany': ['germany'],
  'Netherlands': ['netherlands', 'the netherlands'],
  'Ireland': ['ireland', 'republic of ireland'],
  'Singapore': ['singapore'],
  'Hong Kong': ['hong kong'],
  'South Africa': ['south africa'],
  'United Arab Emirates': ['united arab emirates', 'uae'],
  'Spain': ['spain'],
  'Italy': ['italy'],
  'India': ['india'],
  'Japan': ['japan'],
  'Brazil': ['brazil'],
  'Mexico': ['mexico'],
  'Belgium': ['belgium'],
  'Sweden': ['sweden'],
  'Norway': ['norway'],
  'Denmark': ['denmark'],
  'Portugal': ['portugal'],
  'Poland': ['poland'],
  'Austria': ['austria'],
  'Switzerland': ['switzerland'],
  'Finland': ['finland'],
  'Malaysia': ['malaysia'],
  'Indonesia': ['indonesia'],
  'Philippines': ['philippines'],
  'Thailand': ['thailand'],
  'Vietnam': ['vietnam'],
  'South Korea': ['south korea', 'korea'],
  'Taiwan': ['taiwan'],
  'Israel': ['israel'],
  'Saudi Arabia': ['saudi arabia'],
  'Egypt': ['egypt'],
  'Nigeria': ['nigeria'],
  'Kenya': ['kenya'],
  'Ghana': ['ghana'],
  'Chile': ['chile'],
  'Colombia': ['colombia'],
  'Argentina': ['argentina'],
};

function matchCountry(dealCountry, geoName) {
  if (!dealCountry || !geoName) return false;
  const d = String(dealCountry).trim().toLowerCase();
  const g = String(geoName).trim().toLowerCase();
  if (d === g) return true;
  for (const [canonical, aliases] of Object.entries(COUNTRY_ALIASES)) {
    const matchesDeal = aliases.includes(d) || canonical.toLowerCase() === d;
    const matchesGeo = aliases.includes(g) || canonical.toLowerCase() === g;
    if (matchesDeal && matchesGeo) return true;
  }
  return d === g;
}

function findGeoId(geographies, countryName) {
  const found = geographies?.find(
    (geo) =>
      matchCountry(countryName, geo.properties?.name) ||
      matchCountry(countryName, geo.properties?.NAME) ||
      matchCountry(countryName, geo.id)
  );
  return found?.rsmKey || found?.id;
}

function ProcurementMap({
  deals,
  metric = 'deals', // 'deals' | 'value'
  onMetricChange,
  selectedCountries = [],
  mapZoomCountry = null,
  onMapCountryClick,
  onClearMapZoom,
  onCountrySelect,
  onClearSelection,
}) {
  const { t } = useTranslation();
  const useMapZoom = onMapCountryClick != null;
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 20]);
  const [lastClickedGeoName, setLastClickedGeoName] = useState(null);

  const { byCountry, topCategoriesByCountry } = useMemo(() => {
    const byCountry = {};
    const topCategoriesByCountry = {};

    deals.forEach((d) => {
      const country = d.country || d.institution?.country || 'Unknown';
      if (!country || country === 'Unknown') return;
      const key = country.trim();
      if (!byCountry[key]) {
        byCountry[key] = { count: 0, totalValueUSD: 0, withValue: 0 };
      }
      byCountry[key].count += 1;
      const usdVal = getDealValueUSDForTotals(d);
      if (usdVal > 0) {
        byCountry[key].totalValueUSD += usdVal;
        byCountry[key].withValue += 1;
      }

      const cats = getDealCategories(d);
      const cat = cats[0] ? (toOfficialCategoryName(cats[0]) || cats[0]) : (toOfficialCategoryName(d.category) || d.category || 'Uncategorized');
      if (!topCategoriesByCountry[key]) topCategoriesByCountry[key] = {};
      topCategoriesByCountry[key][cat] = (topCategoriesByCountry[key][cat] || 0) + 1;
    });

    Object.keys(topCategoriesByCountry).forEach((c) => {
      const cats = topCategoriesByCountry[c];
      topCategoriesByCountry[c] = Object.entries(cats)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name]) => name);
    });

    return { byCountry, topCategoriesByCountry };
  }, [deals]);

  const maxCount = useMemo(() => Math.max(...Object.values(byCountry).map((v) => v.count), 1), [byCountry]);
  const maxValue = useMemo(() => Math.max(...Object.values(byCountry).map((v) => v.totalValueUSD), 1), [byCountry]);

  const { totalDeals, totalValueUSD, totalCountries } = useMemo(() => {
    let count = 0;
    let sum = 0;
    const countrySet = new Set();
    deals.forEach((d) => {
      count += 1;
      sum += getDealValueUSDForTotals(d);
      const country = d.country || d.institution?.country || '';
      if (country && country !== 'Unknown') {
        countrySet.add(country.trim());
      }
    });
    return { totalDeals: count, totalValueUSD: sum, totalCountries: countrySet.size };
  }, [deals]);

  const getFill = (countryName) => {
    const data =
      byCountry[countryName] ||
      byCountry[Object.keys(byCountry).find((k) => matchCountry(k, countryName))];
    if (!data) return '#f1f5f9';
    const v = metric === 'value' ? data.totalValueUSD : data.count;
    const max = metric === 'value' ? maxValue : maxCount;
    const ratio = Math.min(v / max, 1);
    const opacity = 0.3 + ratio * 0.6;
    const base = '#00C2C2';
    return `${base}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  };

  const handleMouseEnter = (geo, evt) => {
    const name = geo.properties?.name || geo.properties?.NAME || geo.id || 'Unknown';
    const data = byCountry[name] || byCountry[Object.keys(byCountry).find((k) => matchCountry(k, name))];
    if (!data) {
      setTooltipContent(`${name}: ${t('procMapNoDeals')}`);
    } else {
      const cats = topCategoriesByCountry[name] || topCategoriesByCountry[Object.keys(topCategoriesByCountry).find((k) => matchCountry(k, name))] || [];
      setTooltipContent(
        `${name}\n` +
          `${t('deals')}: ${data.count}\n` +
          `${t('procMapTotalValueUsd')}: ${data.totalValueUSD > 0 ? '$' + data.totalValueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}\n` +
          (cats.length ? `${t('procMapTop')}: ${cats.join(', ')}` : '')
      );
    }
    setTooltipPosition({ x: evt.clientX, y: evt.clientY });
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
  };

  const handleClick = (geo) => {
    const name = geo.properties?.name || geo.properties?.NAME || geo.id;
    if (name && (byCountry[name] || Object.keys(byCountry).some((k) => matchCountry(k, name)))) {
      setLastClickedGeoName(name);
      const matched = Object.keys(byCountry).find((k) => matchCountry(k, name)) || name;
      if (useMapZoom) {
        onMapCountryClick?.(matched);
      } else {
        onCountrySelect?.(matched);
      }
    }
  };

  const isCountrySelected = (geoName) => {
    if (useMapZoom && mapZoomCountry) {
      return matchCountry(mapZoomCountry, geoName);
    }
    return Array.isArray(selectedCountries) && selectedCountries.some((c) => matchCountry(c, geoName));
  };

  const zoomSource = useMapZoom ? mapZoomCountry : (selectedCountries?.length ? selectedCountries[selectedCountries.length - 1] : null);

  useEffect(() => {
    if (zoomSource) {
      const geoName = lastClickedGeoName || zoomSource;
      const c = getCentroid(geoName);
      if (c) {
        setCenter(c);
        setZoom(4);
      }
    } else {
      setLastClickedGeoName(null);
      setCenter([0, 20]);
      setZoom(1);
    }
  }, [zoomSource, lastClickedGeoName]);

  return (
    <div className="relative w-full rounded-lg border border-gray-200 bg-white overflow-hidden">
      {onMetricChange && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">{t('procMapMetric')}</span>
          <button
            onClick={() => onMetricChange('deals')}
            className={`text-xs px-2 py-1 rounded ${metric === 'deals' ? 'bg-[#00C2C2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('deals')}
          </button>
          <button
            onClick={() => onMetricChange('value')}
            className={`text-xs px-2 py-1 rounded ${metric === 'value' ? 'bg-[#00C2C2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('procMapTotalValue')}
          </button>
        </div>
      )}
      <div className="absolute bottom-2 right-2 z-10 flex flex-col gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setZoom((z) => Math.min(z + 0.5, 8))}
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setZoom((z) => Math.max(z - 0.5, 0.5))}
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
      </div>
      <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-2">
        <div className="bg-white/95 backdrop-blur rounded-lg border-2 border-gray-300 px-5 py-3 shadow-md min-w-[140px]">
          <div className="text-sm text-gray-600 mb-1">{t('procMapCountries')}</div>
          <div className="text-lg font-bold text-[#0A1A2F]">
            {totalCountries.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 mt-2 mb-1">{t('deals')}</div>
          <div className="text-lg font-bold text-[#0A1A2F]">
            {totalDeals.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 mt-2 mb-1">{t('procMapTotalValue')}</div>
          <div className="text-lg font-bold text-[#0A1A2F]">
            {totalValueUSD > 0 ? '$' + totalValueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'}
          </div>
        </div>
        {((useMapZoom && mapZoomCountry) || (selectedCountries?.length > 0)) && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 gap-1"
            onClick={useMapZoom ? onClearMapZoom : onClearSelection}
          >
            <X className="w-3 h-3" />
            {t('procMapClearSelection')}
          </Button>
        )}
      </div>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 120 }}
        className="w-full"
        style={{ minHeight: 360, height: 'min(45vh, 480px)' }}
      >
        <ZoomableGroup
          center={center}
          zoom={zoom}
          onMoveEnd={({ coordinates, zoom: z }) => {
            setCenter(coordinates);
            setZoom(z);
          }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties?.name || geo.properties?.NAME || geo.id;
                const fill = getFill(name);
                const isSelected = isCountrySelected(name);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke={isSelected ? '#0A1A2F' : '#e2e8f0'}
                    strokeWidth={isSelected ? 1.5 : 0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', cursor: 'pointer' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleClick(geo)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      {tooltipContent && (
        <div
          className="fixed z-[9999] px-3 py-2 text-xs bg-[#0A1A2F] text-white rounded shadow-lg pointer-events-none whitespace-pre-line max-w-[200px]"
          style={{ left: tooltipPosition.x + 10, top: tooltipPosition.y + 10 }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
}

export default React.memo(ProcurementMap);
